// Bounded memory cache shared by screen reads. Never persists financial data.
import LocalstorageService from '../helpers/localstorage-service';
import { AuthUserInfo, CompanyId, JobRole, UserId } from '../constants';
import { recordScreenPerformance } from '../helpers/screenPerformance';

const DEFAULT_TTL_MS = 5 * 60 * 1000;
const MAX_ENTRIES = 100;
const store = new Map();
const pending = new Map();
let scope;
let generation = 0;

// Includes the current session: logging out/back in to the same company must
// not reuse the previous user's data. This signature is never logged or saved.
const currentScope = () => JSON.stringify([
  LocalstorageService.getItem(CompanyId), LocalstorageService.getItem(UserId),
  LocalstorageService.getItem(JobRole), LocalstorageService.getItem(AuthUserInfo)?.token || null,
]);
const guard = () => {
  const next = currentScope();
  if (next !== scope) {
    scope = next;
    generation += 1;
    store.clear();
    pending.clear();
  }
};
const copy = (response) => response?.data === undefined ? response
  : ({ ...response, data: JSON.parse(JSON.stringify(response.data)) });
const canceled = () => Object.assign(new Error('Screen request superseded'), { code: 'ERR_CANCELED' });

export const requestCacheKey = (prefix, value) => {
  const normalize = (v) => Array.isArray(v) ? v.map(normalize)
    : v && typeof v === 'object'
      ? Object.fromEntries(Object.keys(v).sort().map(key => [key, normalize(v[key])])) : v;
  return prefix + ':' + JSON.stringify(normalize(value));
};
export const cacheGet = (key) => {
  guard();
  const entry = store.get(key);
  if (!entry || entry.expiresAt <= Date.now()) { store.delete(key); return null; }
  store.delete(key); store.set(key, entry);
  return copy(entry.data);
};
export const cacheSet = (key, data, ttlMs = DEFAULT_TTL_MS) => {
  guard();
  if (store.size >= MAX_ENTRIES && !store.has(key)) store.delete(store.keys().next().value);
  store.set(key, { data: copy(data), expiresAt: Date.now() + ttlMs });
};
export const cacheInvalidate = (prefix) => {
  guard();
  const prefixes = Array.isArray(prefix) ? prefix : [prefix];
  const matches = key => prefixes.some(p => key.startsWith(p));
  for (const key of store.keys()) if (matches(key)) store.delete(key);
  for (const [key, flight] of pending) if (matches(key)) {
    flight.invalidated = true;
    pending.delete(key);
  }
};
export const cacheInvalidateAll = () => {
  guard(); generation += 1; store.clear(); pending.clear();
};
export const cacheSize = () => { guard(); return store.size; };

export const withCache = (fetchFn, keyFn, ttlMs = DEFAULT_TTL_MS) => (...args) => {
  guard();
  const key = keyFn(...args);
  const cached = cacheGet(key);
  if (cached !== null) {
    recordScreenPerformance({ kind: 'cache', source: key.split(':')[0], hit: true });
    return Promise.resolve(cached);
  }
  const existing = pending.get(key);
  if (existing) return existing.promise.then(copy);
  const epoch = generation;
  const flight = { invalidated: false };
  flight.promise = Promise.resolve().then(() => fetchFn(...args)).then(response => {
    guard();
    if (epoch !== generation || flight.invalidated) throw canceled();
    if (response?.data && (response.status == null || response.status < 400)
        && !(Number(response.data.status) >= 400)) cacheSet(key, response, ttlMs);
    recordScreenPerformance({ kind: 'cache', source: key.split(':')[0], hit: false });
    return response;
  }).finally(() => { if (pending.get(key) === flight) pending.delete(key); });
  pending.set(key, flight);
  return flight.promise.then(copy);
};
